import { Link } from "react-router-dom";
import {
  FaLeaf,
  FaPalette,
  FaCheckCircle,
  FaRocket,
  FaArrowRight,
  FaStore,
  FaUsers,
  FaUserTie,
  FaChartLine,
  FaTshirt,
  FaBabyCarriage,
} from "react-icons/fa";
import "../styles/AboutPage.css";

export default function AboutPage() {
  const stats = [
    { number: "63+", label: "Years of Heritage", icon: <FaBabyCarriage /> },
    { number: "10,000+", label: "Happy Customers", icon: <FaUsers /> },
    { number: "600+", label: "Whole Sellers", icon: <FaStore /> },
    { number: "101+", label: "Products", icon: <FaTshirt /> },
    { number: "75+", label: "Employees", icon: <FaUserTie /> },
    { number: "27,000+", label: "Pincode Coverage", icon: <FaChartLine /> },
  ];

  const values = [
    {
      icon: <FaLeaf />,
      title: "Pure Cotton",
      desc: "100% combed compact cotton — skin-friendly, breathable, and built to last generations.",
    },
    {
      icon: <FaPalette />,
      title: "Bold Designs",
      desc: "From timeless classics to vivid prints — patterns that carry a story in every thread.",
    },
    {
      icon: <FaCheckCircle />,
      title: "Quality First",
      desc: "Every product is inspected for colour, weave, and finish before it reaches your hands.",
    },
    {
      icon: <FaRocket />,
      title: "Innovation",
      desc: "Pioneers of washed ready-to-wear dhotis — always pushing what textile can be.",
    },
  ];

  return (
    <div className="about-page">
      {/* ── CINEMATIC HERO ─────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-grain" />
        <div className="hero-content">
          <p className="hero-since">— Est. 1961 —</p>
          <h1 className="hero-title">
            <span className="hero-title-line">KKB &amp; Co.</span>
            <em className="hero-title-italic">Diamond Jubilee</em>
            <span className="hero-title-line">in Textile Excellence</span>
          </h1>
          <p className="hero-sub">
            Unconquerable. Trusted. Woven into generations.
          </p>
          <Link to="/collection" className="hero-cta">
            Explore Collections <FaArrowRight />
          </Link>
        </div>
        <div className="hero-scroll-hint">
          <span>Scroll</span>
          <div className="hero-scroll-line" />
        </div>
      </section>

      {/* ── MARQUEE STRIP ──────────────────────────────────── */}
      <div className="marquee-strip">
        <div className="marquee-track">
          {[
            "Pure Cotton",
            "Since 1961",
            "Printed Lungi Pioneers",
            "63+ Years",
            "Trusted Quality",
            "Made in India",
            "Pure Cotton",
            "Since 1961",
            "Printed Lungi Pioneers",
            "63+ Years",
            "Trusted Quality",
            "Made in India",
          ].map((t, i) => (
            <span key={i} className="marquee-item">
              {t} <span className="marquee-dot">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── WHO WE ARE — EDITORIAL SPLIT ───────────────────── */}
      <section className="editorial-section">
        <div className="editorial-left">
          <div className="editorial-img-frame">
            <img
              src="https://kkbstore.com/wp-content/uploads/2023/12/2023-05-30.jpg"
              alt="KKB Textiles"
            />
            <div className="editorial-year-badge">
              <span>Est.</span>
              <strong>1961</strong>
            </div>
          </div>
        </div>
        <div className="editorial-right">
          <span className="eyebrow">Who We Are</span>
          <h2 className="section-heading">
            Diamond Jubilee
            <br />
            <em>in Textile Excellence</em>
          </h2>
          <div className="ruled-line" />
          <p className="lede">
            The word <em>'Diamond'</em> originates from the Greek{" "}
            <em>'adamus'</em> — meaning <em>'unconquerable'</em>. It is the
            perfect epithet for our journey.
          </p>
          <p className="body-copy">
            Proudly completing a Diamond Jubilee in the textile field, KKB &amp;
            Co. has grown from a humble beginning of under one lakh in turnover
            to a multicrore enterprise — built on a spirit of trust, quality,
            and relentless perseverance. We continuously reinvent ourselves in
            tune with a changing world, while holding true to the values that
            have defined us for over six decades.
          </p>
          <Link to="/collection" className="text-link">
            Shop Our Collections <FaArrowRight className="link-icon" />
          </Link>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────── */}
      <section className="stats-section">
        <div className="stats-inner">
          <div className="stats-heading-col">
            <span className="eyebrow light">By the Numbers</span>
            <h2 className="stats-title">
              Six Decades
              <br />
              of Impact
            </h2>
            <div className="stats-accent-bar" />
          </div>
          <div className="stats-grid">
            {stats.map((s) => (
              <div className="stat-card" key={s.label}>
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-number">{s.number}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOUNDER ────────────────────────────────────────── */}
      <section className="profile-section profile-section--light">
        <div className="profile-img-col">
          <div className="profile-photo-wrap">
            <img
              src="https://kkbstore.com/wp-content/uploads/2023/12/Screenshot-2023-12-09-104033.jpg"
              alt="Mr. K.K. Balusamy"
            />
            <div className="profile-overlay-tag">
              <div className="profile-name">MR. K.K. BALUSAMY</div>
              <div className="profile-role">CEO &amp; Founder</div>
            </div>
          </div>
        </div>
        <div className="profile-content-col">
          <span className="eyebrow">About the Founder</span>
          <h2 className="section-heading">
            Saadhanai Chemmal
            <br />
            <em>The Kaili King</em>
          </h2>
          <div className="ruled-line" />
          <p className="lede">
            Born January 1946, he sacrificed his dream of becoming a surgeon at
            age 16 to venture into textiles — and changed an entire industry.
          </p>
          <p className="body-copy">
            Fondly known as <strong>'Kaili King'</strong>, he is credited with
            the invention of the
            <strong> Printed Lungi</strong> — a breakthrough that transformed
            the lungi market forever. He introduced wax print, art print, spray
            print, and Procion print to the market, and evolved printing methods
            from Wooden Block to Table to Rotary.
          </p>
          <p className="body-copy">
            An ingrained philanthropist and known as{" "}
            <strong>'Kalvi Thanthai'</strong>, he never hoarded knowledge — he
            shared it freely. Today, he has handed over the reins to his
            children while serving society through educational institutions.
          </p>
        </div>
      </section>

      {/* ── LEADERSHIP DIVIDER ─────────────────────────────── */}
      <div className="divider-band">
        <div className="divider-band-inner">
          <div className="divider-line" />
          <span>Our Leadership</span>
          <div className="divider-line" />
        </div>
      </div>

      {/* ── MANAGING DIRECTOR ──────────────────────────────── */}
      <section className="profile-section profile-section--cream profile-section--flip">
        <div className="profile-content-col">
          <span className="eyebrow">Managing Director</span>
          <h2 className="section-heading">
            Mr. Arun Kumar
            <br />
            <em>Balusamy</em>
          </h2>
          <div className="ruled-line" />
          <p className="lede">
            A BBM graduate from PSG Coimbatore, born 1979, he joined the family
            legacy and transformed it into a vertically integrated powerhouse.
          </p>
          <p className="body-copy">
            A strong believer in quality and trust, he expanded the product line
            into knitted garments, formals, dhotis, and Home Tex products. His
            sharp acumen for market shifts led him to establish own weaving
            units — making KKB a self-reliant manufacturer of grey material,
            knitting, home tex, and woven products without compromise in
            quality.
          </p>
        </div>
        <div className="profile-img-col">
          <div className="profile-photo-wrap">
            <img
              src="https://kkbstore.com/wp-content/uploads/2023/12/Screenshot-2023-12-09-103934.jpg"
              alt="Mr. Arun Kumar Balusamy"
            />
            <div className="profile-overlay-tag">
              <div className="profile-name">MR. ARUNKUMAR BALUSAMY</div>
              <div className="profile-role">Managing Director</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXECUTIVE DIRECTOR ─────────────────────────────── */}
      <section className="profile-section profile-section--dark">
        <div className="profile-img-col">
          <div className="profile-photo-wrap">
            <img
              src="https://kkbstore.com/wp-content/uploads/2023/12/Screenshot-2023-12-09-103911.jpg"
              alt="Mrs. Manjula Vidhya Shankar"
            />
            <div className="profile-overlay-tag">
              <div className="profile-name">MRS. MANJULA VIDHYA SHANKAR</div>
              <div className="profile-role">Executive Director</div>
            </div>
          </div>
        </div>
        <div className="profile-content-col">
          <span className="eyebrow">Executive Director</span>
          <h2 className="section-heading">
            Mrs. Manjula
            <br />
            <em>Vidhya Shankar</em>
          </h2>
          <div className="ruled-line" />
          <p className="lede">
            Born 1976, pursuing BA English Literature — a natural leader whose
            entrepreneurial venture began in 2007.
          </p>
          <p className="body-copy">
            Serving as Executive Director, her unique comprehension of business
            and deep appreciation for garments drives a passionate mission to
            carve a lasting market presence. A voracious reader and strong
            advocate of systematic order, she has streamlined administration and
            brought futuristic thinking — her resourcefulness and novel ideas
            continuously adding confidence and strength to KKB &amp; Co.
          </p>
        </div>
      </section>

      {/* ── PRODUCTION TIMELINE ────────────────────────────── */}
      <section className="production-section">
        <div className="production-container">
          <div className="production-header">
            <span className="eyebrow">Our Process</span>
            <h2 className="section-heading" style={{ textAlign: "center" }}>
              Line of Production
            </h2>
            <p className="production-sub">
              How we evolved from trader to integrated manufacturer
            </p>
          </div>

          <div className="production-eras">
            <div className="era era--before">
              <div className="era-label">
                <div className="era-tag">Before MD Stepping</div>
              </div>
              <div className="era-steps">
                {["Purchase", "Printing", "Folding", "Packing", "Dispatch"].map(
                  (step, i) => (
                    <div className="era-step" key={step}>
                      <div className="era-step-node">
                        <span className="era-step-num">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="era-step-label">{step}</div>
                      {i < 4 && <div className="era-step-connector" />}
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="era-divider">
              <div className="era-divider-line" />
              <div className="era-divider-diamond">◆</div>
              <div className="era-divider-line" />
            </div>

            <div className="era era--after">
              <div className="era-label">
                <div className="era-tag era-tag--gold">After MD Stepping</div>
              </div>
              <div className="era-steps">
                {[
                  "Yarn Procurement",
                  "Weaving",
                  "Printing",
                  "Standard Folding",
                  "Machine Packing",
                  "Dispatch",
                ].map((step, i) => (
                  <div className="era-step" key={step}>
                    <div className="era-step-node era-step-node--gold">
                      <span className="era-step-num">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="era-step-label">{step}</div>
                    {i < 5 && (
                      <div className="era-step-connector era-step-connector--gold" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ─────────────────────────────────────────── */}
      <section className="values-section">
        <div className="values-header">
          <span className="eyebrow">Our Values</span>
          <h2 className="section-heading" style={{ textAlign: "center" }}>
            The principles that guide
            <br />
            <em>every thread we weave</em>
          </h2>
        </div>
        <div className="values-grid">
          {values.map((v, i) => (
            <div className="value-card" key={v.title}>
              <div className="value-card-num">0{i + 1}</div>
              <div className="value-icon">{v.icon}</div>
              <h3 className="value-title">{v.title}</h3>
              <p className="value-desc">{v.desc}</p>
              <div className="value-card-line" />
            </div>
          ))}
        </div>
      </section>

      {/* ── VIDEO ──────────────────────────────────────────── */}
      <section className="video-section">
        <div className="video-section-inner">
          <div className="video-text">
            <span className="eyebrow">Our Story in Motion</span>
            <h2 className="section-heading">
              KKB —<br />
              <em>Introduction</em>
            </h2>
            <p className="body-copy">
              Watch the story of over six decades — a journey of grit,
              innovation, and the unbreakable spirit of a family that changed
              Indian textiles.
            </p>
          </div>
          <div className="video-frame">
            <div className="video-frame-border" />
            <iframe
              src="https://www.youtube.com/embed/-iZm5iCMLyM?controls=1&rel=0"
              title="KKB Introduction"
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────── */}
      <section className="cta-section">
        <div className="cta-section-bg" />
        <div className="cta-content">
          <p className="cta-small">Woven with trust since 1961</p>
          <h2 className="cta-heading">
            Ready to Experience
            <br />
            KKB Comfort?
          </h2>
          <p className="cta-sub">
            Browse our latest collections and find your perfect style.
          </p>
          <Link to="/collection" className="cta-btn">
            Shop All Products <FaArrowRight className="cta-btn-icon" />
          </Link>
        </div>
      </section>
    </div>
  );
}
